import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { LuCalendarCheck, LuCalendarClock, LuRotateCcw, LuSearch, LuShip, LuUser, LuX } from 'react-icons/lu';
import { MdFilterAlt } from 'react-icons/md';
import { DateRangePicker, SegmentedControl, TwDropdown } from 'components/common';
import { ShipScopeBadge, shipScopeOptions, useShipScopes } from 'components/app/crm/logistic/shipScope';
import { EmployeeSelect } from 'components/common/select/EmployeeSelectField';
import { filterOptions as employeeOptions } from 'helpers/employeeOptionHelper';
import { STATUS_ICONS, STATUS_LABELS } from 'components/app/crm/dashboard/logistic/logisticStatus';
import StatusLegend from 'components/app/crm/dashboard/logistic/StatusLegend';

// Trigger labels stay short so the control never truncates; the menu hint carries
// the distinction between "departed at all" and "departed on time", which is the
// one place in this screen where the same word means two things.
export const STATUS_OPTIONS = [
  { value: '', label: 'All statuses', icon: MdFilterAlt },
  { value: 'shipped', label: 'Departed', icon: STATUS_ICONS.shipped, hint: 'on time + late' },
  { value: 'on_time', label: 'On time', icon: STATUS_ICONS.shipped },
  { value: 'delayed', label: STATUS_LABELS.delayed, icon: STATUS_ICONS.delayed },
  { value: 'pending_booking', label: STATUS_LABELS.pending_booking, icon: STATUS_ICONS.pending_booking },
  { value: 'booked_pending', label: STATUS_LABELS.booked_pending, icon: STATUS_ICONS.booked_pending },
];

const DATE_FILTERS = [
  { key: 'etdRange', label: 'CSD ETD', title: 'CSD request ETD', icon: LuCalendarClock },
  { key: 'bookingRange', label: 'Booking ETD', title: 'Booking ETD', icon: LuShip },
  { key: 'actualRange', label: 'Actual ETD', title: 'Actual ETD', icon: LuCalendarCheck },
];

const FOCUS_RING = 'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-primary-500 focus-visible:tw-ring-offset-1';

// The field name sits beside the pill rather than above it: a caption row over six
// controls turns a toolbar into a form and leaves the controls too narrow to read.
const DateFilter = ({ icon: Icon, label, title, value, onChange }) => (
  <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0" title={title}>
    <Icon size={13} className="tw-text-slate-400" aria-hidden="true" />
    <span className="tw-text-xs tw-font-medium tw-text-slate-500 tw-whitespace-nowrap">{label}</span>
    <DateRangePicker
      label="Any"
      startDate={value?.startDate || ''}
      endDate={value?.endDate || ''}
      onChange={onChange}
    />
  </div>
);

DateFilter.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
};

const LogisticTrackingFilterBar = ({ filters, onChange, onReset, onOpenAdvanced, advancedCount }) => {
  // The LOGISTIC owner of the shipment (crm_logistics.incharge, a three-character
  // employee code), not the project's sales in-charge — so the picker is the whole
  // employee directory rather than the sales list.
  const loadEmployees = useCallback(
    input => employeeOptions('/organization/employees/public_list?', input, true, true),
    []
  );

  // A user limited to one scope gets a fixed badge instead of a switch.
  const shipScopes = useShipScopes();
  const canSwitchScope = shipScopes.length > 1;

  const dateCount = DATE_FILTERS.filter(({ key }) => filters[key]?.startDate).length;
  const activeCount = dateCount
    + (filters.search ? 1 : 0)
    + (filters.shipScope ? 1 : 0)
    + (filters.incharge?.length ? 1 : 0)
    + (filters.status ? 1 : 0)
    + advancedCount;

  return (
    <div role="search" className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-solid tw-border-slate-200 tw-px-3 tw-py-2.5">
      {/* One toolbar row: search, the three dates, in-charge and status, with the
          actions pushed to the end. It wraps as a whole rather than stranding a
          single control on a line of its own. */}
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2">
        <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-60 sm:tw-flex-shrink-0">
          <LuSearch className="tw-absolute tw-left-2.5 tw-text-slate-400 tw-pointer-events-none" size={13} aria-hidden="true" />
          <input
            type="search"
            name="logistic_search"
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="search"
            value={filters.search}
            onChange={event => onChange({ search: event.target.value })}
            placeholder="Job#, buyer, phase name…"
            aria-label="Search by job number, buyer or phase name"
            className={`tw-w-full tw-h-8 tw-pl-8 tw-pr-7 tw-text-xs tw-border tw-border-solid tw-border-slate-300 tw-rounded tw-bg-white tw-text-slate-700 tw-placeholder-slate-400 ${FOCUS_RING}`}
          />
          {filters.search && (
            <button
              type="button"
              onClick={() => onChange({ search: '' })}
              aria-label="Clear search"
              className={`tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-p-0 tw-touch-manipulation ${FOCUS_RING}`}
            >
              <LuX size={9} />
            </button>
          )}
        </div>

        {canSwitchScope ? (
          <SegmentedControl
            size="sm"
            ariaLabel="Local or Oversea shipments"
            value={filters.shipScope || ''}
            onChange={value => onChange({ shipScope: value })}
            options={shipScopeOptions()}
          />
        ) : (
          <ShipScopeBadge scope={shipScopes[0]} />
        )}

        <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0 tw-hidden xl:tw-block" aria-hidden="true" />

        {DATE_FILTERS.map(({ key, label, title, icon }) => (
          <DateFilter
            key={key}
            icon={icon}
            label={label}
            title={title}
            value={filters[key]}
            onChange={({ startDate, endDate }) => onChange({ [key]: { startDate, endDate } })}
          />
        ))}

        <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0 tw-hidden xl:tw-block" aria-hidden="true" />

        {/* Logistic in-charge — multi, avatars, matched to the 32px toolbar height */}
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-flex-shrink-0">
          <LuUser size={13} className="tw-text-slate-400" aria-hidden="true" />
          <span className="tw-text-xs tw-font-medium tw-text-slate-500 tw-whitespace-nowrap">In-charge</span>
          <div className="tw-w-44">
            <EmployeeSelect
              isMulti
              summarizeSelection
              size="sm"
              value={filters.incharge}
              onChange={option => onChange({ incharge: option || [] })}
              loadOptions={loadEmployees}
              placeholder="All"
            />
          </div>
        </div>

        <div className="tw-flex tw-items-center tw-gap-0.5 tw-flex-shrink-0">
          <TwDropdown
            size="sm"
            value={filters.status}
            onChange={value => onChange({ status: value })}
            options={STATUS_OPTIONS}
            leftIcon={STATUS_ICONS[filters.status] || MdFilterAlt}
            placeholder="All statuses"
            minWidth="150px"
            menuWidth="220px"
          />
          {/* The four states are rules, not adjectives — say what each one tests. */}
          <StatusLegend />
        </div>

        {/* Actions sit at the end of the row: filters first, then what you do with them */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto tw-flex-shrink-0">
          <p className="tw-text-xs tw-text-slate-400 tw-m-0 tw-hidden sm:tw-block" aria-live="polite">
            {activeCount === 0 ? 'No filter applied' : `${activeCount} filter${activeCount === 1 ? '' : 's'} applied`}
          </p>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onReset}
              className={`tw-inline-flex tw-items-center tw-gap-1 tw-h-8 tw-px-2 tw-rounded tw-border-0 tw-bg-transparent tw-text-xs tw-font-medium tw-text-slate-500 hover:tw-text-red-600 hover:tw-bg-slate-50 tw-cursor-pointer tw-touch-manipulation ${FOCUS_RING}`}
            >
              <LuRotateCcw size={12} aria-hidden="true" /> Clear all
            </button>
          )}
          <button
            type="button"
            onClick={onOpenAdvanced}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-2.5 tw-rounded tw-border tw-border-solid tw-text-xs tw-font-semibold tw-cursor-pointer tw-whitespace-nowrap tw-touch-manipulation tw-transition-colors ${FOCUS_RING} ${
              advancedCount > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-300 tw-text-slate-600 hover:tw-border-primary-400 hover:tw-text-primary-600'
            }`}
          >
            <MdFilterAlt size={14} aria-hidden="true" />
            More filters
            {advancedCount > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[16px] tw-h-4 tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-[10px] tw-font-bold tw-leading-none tw-tabular-nums">
                {advancedCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

LogisticTrackingFilterBar.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    etdRange: PropTypes.object,
    bookingRange: PropTypes.object,
    actualRange: PropTypes.object,
    incharge: PropTypes.array,
    status: PropTypes.string,
    shipScope: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onOpenAdvanced: PropTypes.func.isRequired,
  advancedCount: PropTypes.number,
};

LogisticTrackingFilterBar.defaultProps = { advancedCount: 0 };

export default LogisticTrackingFilterBar;
